import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { ExploreCurriculum } from 'stile-shared/src/components/2024/common/ExploreCurriculum';
import { IconSet } from 'stile-shared/src/components/2024/common/IconSet/IconSet';
import { Link } from 'stile-shared/src/components/2024/common/Link';
import PedagogyHeader from 'stile-shared/src/components/2024/common/PedagogyHeader';
import PedagogyWithinStileCard from 'stile-shared/src/components/2024/common/PedagogyWithinStileCard';
import { SubNav, SubNavItem } from 'stile-shared/src/components/2024/common/SubNav';
import { Grid } from 'stile-shared/src/components/2024/layout/Grid';
import { Section } from 'stile-shared/src/components/2024/layout/Section';

import { PageLayout } from 'templates/2024/PageLayout';

const ExamplesSection = styled<typeof Section>(Section)`
  margin-top: 0 !important;
  grid-column: span 12;

  .content {
    padding-top: 0 !important;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      padding-top: 0 !important;
      margin-bottom: 69px !important;
      margin: 0 25px;
    }
  }

  position: relative;

  h6 {
    color: var(--stile-green-3);
    margin: 0;
    margin-bottom: 10px;
    grid-column: span 12;
    @media (max-width: 768px) {
      margin-bottom: 0 !important;
    }
  }

  .grid-layout {
    margin-top: 0 !important;
    grid-column: 1 / span 12;
  }

  > .grid-layout {
    align-items: center;
    margin-bottom: 150px;
    @media (max-width: 768px) {
      margin-bottom: 80px;
    }
  }
`;

export { Head } from 'templates/2024/Head';

function SoloTaxonomyPage(props: PageProps) {
  const exploreCurriculumImage = (
    <StaticImage
      src="../../components/our-principles/images/teachersaying_quality_4.png"
      alt="Stacked documents with celestial diagram showing sun and orbiting planets"
      placeholder="blurred"
      layout="constrained"
      width={124}
      height={124}
    />
  );

  const subNavItems: SubNavItem[] = [
    {
      label: '5E Instructional Model',
      url: '/pedagogies/5e-instructional-model/',
    },
    {
      label: 'Phenomena-based Learning',
      url: '/pedagogies/phenomena-based-learning/',
    },
    {
      label: 'Three-dimensional Learning',
      url: '/pedagogies/three-dimensional-learning/',
    },
    {
      label: 'SOLO Taxonomy',
      url: '/pedagogies/solo-taxonomy/',
      isActive: true,
    },
    {
      label: 'Universal Design for Learning',
      url: '/pedagogies/universal-design/',
    },
    {
      label: 'Explicit Instruction',
      url: '/pedagogies/explicit-instruction/',
    },
    {
      label: 'Inquiry-based Learning',
      url: '/pedagogies/inquiry-based-learning/',
    },
    {
      label: 'Cognitive Load Theory',
      url: '/pedagogies/cognitive-load-theory/',
    },
    {
      label: 'Differentiated Instruction',
      url: '/pedagogies/differentiated-instruction/',
    },
  ];

  return (
    <PageLayout {...props}>
      <SubNav items={subNavItems} />

      <PedagogyHeader
        title="SOLO taxonomy"
        description="SOLO Taxonomy describes the developmental progression of learning through five levels of understanding: Pre-structural, Uni-structural, Multi-structural, Relational and Extended-abstract. Its structure, along with verbs associated with each level of the taxonomy, guide learning design that supports student mastery and metacognition."
        iconBullets={[
          {
            iconSrc: IconSet['lightonIcon0404'],
            title: 'Initially developed by',
            text: 'National Research Council (NGSS writers), USA.',
          },
          {
            iconSrc: IconSet['conflictIcon0505'],
            title: 'Effectiveness',
            text: (
              <>
                John Hattie's Visible Learning and the Science of How We Learn (2013) describes SOLO
                Taxonomy as "the most powerful model" for understanding students' learning.
              </>
            ),
          },
          {
            iconSrc: IconSet['glossaryIcon0705'],
            title: 'Further reading',
            text: (
              <Link
                url="https://nap.nationalacademies.org/catalog/13165/a-framework-for-k-12-science-education-practices-crosscutting-concepts"
                openInNewTab
              >
                NRC Framework for K-12 Science Education (2012)
              </Link>
            ),
          },
          {
            iconSrc: IconSet['locationIcon0704'],
            title: "Where you'll find it in Stile",
            text: 'Every question in Stile uses a verb that connects with the levels of understanding in SOLO. Within a lesson, questions build understanding through the SOLO progression. Units culminate in assessments that allow students to demonstrate extended-abstract thinking.',
          },
        ]}
      />

      <ExamplesSection>
        <h6>Examples within Stile</h6>
        <Grid>
          <PedagogyWithinStileCard
            id="socratic-seminar"
            title="Socratic seminar"
            description="Students apply their cumulative knowledge of energy conservation to research, discuss and make recommendations regarding the proposed Congo dam."
            url="http://stileapp.com/go/solo-example-1"
            subtitle={
              <div>
                <em>Socratic seminar: The Congo dam project</em> lesson
                <br />
                in the Energy Conservation unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/solo_socratic.png"
                alt="Text: Discuss whether the Congo dam should go ahead in a Socratic seminar. Image: Illustration of diverse participants including a local villager, resident of South Africa, scientist, and engineer discussing around a seminar circle diagram."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="engineering-challenge"
            title="Engineering challenge"
            description="Students use their understanding of ecosystems and a global declining bee population to engineer a solution over a series of lessons."
            url="http://stileapp.com/go/solo-example-2"
            subtitle={
              <div>
                <em>Defining the problem</em> lesson
                <br />
                in the Importance of Biodiversity unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/solo_engineering.png"
                alt="Text: Engineering challenge. The problem that needs solving is… My goal is to solve this problem by… Image: Illustration of students and scientists working with blueprints, robotics, and a laptop, with bees shown in the bottom left."
                objectFit="contain"
              />
            }
          />
          <PedagogyWithinStileCard
            id="critical-thinking-evaluation"
            title="Critical thinking evaluation"
            description="Students engage critical thinking and build on their understanding of lithium extraction to evaluate the suitability of a new mine."
            url="http://stileapp.com/go/solo-example-3"
            subtitle={
              <div>
                <em>Research: The future of lithium mining</em> lesson
                <br />
                in the Elements and Compounds – Lithium Edition unit
              </div>
            }
            image={
              <StaticImage
                src="../../../../stile-shared/assets/images/pedagogies/solo_evaluate_US.png"
                alt="Text: Evaluate whether a new direct lithium extraction mine would be suitable for the Maricunga salt flats. Image: Diagram showing the process of extracting lithium from a brine-containing aquifer, with labeled steps for separating and filtering minerals."
                objectFit="contain"
              />
            }
          />
        </Grid>
      </ExamplesSection>

      <ExploreCurriculum
        title="Explore our core science curriculum"
        image={exploreCurriculumImage}
        exploreCardsData={[
          {
            title: "Inside Stile's Curriculum",
            description: 'How we make the best science lessons in the known universe.',
            url: '/what-is-stile/middle-school-curriculum/',
          },
          {
            title: "What's Included",
            description:
              'A complete, comprehensive curriculum — used, loved, and perfected by thousands of teachers.',
            url: '/what-is-stile/whats-included/',
          },
          {
            title: 'Why HQIM?',
            description: 'Why High-Quality Instructional Materials make all the difference.',
            url: '/why-choose-stile/high-quality-instructional-materials/',
          },
        ]}
      />
    </PageLayout>
  );
}

export default SoloTaxonomyPage;
